// components/portal/DoctrinePageHistory.tsx
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { DoctrineVersionDTO } from '#types/types';
import { lineDiff } from '#lib/doctrine/lineDiff';

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const MONO: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, lineHeight: '19px' };

/** A page's versions, what each changed compared with the live text, and the way back to one. */
export function DoctrinePageHistory({ sectionKey, systemPage, canRestore, onRestored }: {
  sectionKey: string; systemPage: boolean; canRestore: boolean; onRestored: () => void;
}) {
  const [versions, setVersions] = useState<DoctrineVersionDTO[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Which version is open is view state of this panel alone.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Re-read on every pick as well: the page below autosaves while this is open, and the diff must be
  // against the text that is live now, not the text there was when History opened.
  const load = useCallback((pick?: string) => fetch(`/api/doctrine/history?key=${encodeURIComponent(sectionKey)}`, { cache: 'no-store' })
    .then(res => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
    .then((data: { versions: DoctrineVersionDTO[] }) => ({ versions: data.versions, selected: pick ?? data.versions[1]?.id ?? null })), [sectionKey]);

  useEffect(() => {
    let live = true;
    void load().then(next => { if (live) { setVersions(next.versions); setSelectedId(next.selected); } }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [sectionKey]);

  // Only the latest pick may land: two quick clicks can answer out of order.
  const latestPick = useRef(0);
  const pick = useCallback((id: string) => {
    const request = ++latestPick.current;
    setSelectedId(id);
    void load(id).then(next => { if (request === latestPick.current) { setVersions(next.versions); setSelectedId(next.selected); } })
      .catch(() => { if (request === latestPick.current) setFailed(true); });
  }, [load]);

  const current = versions?.[0];
  const selected = versions?.find(item => item.id === selectedId) ?? null;
  const diff = useMemo(() => (selected && current ? lineDiff(selected.content, current.content) : []), [selected, current]);
  const author = (item: DoctrineVersionDTO) => item.authorName ?? (systemPage ? 'Fater default' : 'Unknown');

  const restore = useCallback(async () => {
    if (!selected) return;
    setRestoring(true);
    const res = await fetch('/api/doctrine/history', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ versionId: selected.id }) }).catch(() => null);
    setRestoring(false);
    if (res?.ok) onRestored();
    else toast.error('This version could not be restored. The page was not changed.');
  }, [selected, onRestored]);

  if (failed) return <p className="mt-4 text-[13px] text-[#909090]">The history could not be loaded.</p>;
  if (!versions) return <p className="mt-4 text-[13px] text-[#6E6E6E]">Loading the history…</p>;
  return (
    <div className="mt-4 flex overflow-hidden rounded-lg border border-[#2A2A2A] bg-[#141414]" style={{ maxHeight: '60vh' }}>
      <ol className="w-[220px] shrink-0 overflow-y-auto border-r border-[#2A2A2A] py-1">
        {versions.map(item => (
          <li key={item.id}>
            <button type="button" disabled={item.current} onClick={() => pick(item.id)}
              className={`flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left transition-colors duration-150 ${item.id === selectedId ? 'bg-[#1E3563]' : item.current ? '' : 'hover:bg-white/5'}`}>
              <span className="text-[13px] text-[#E8E8E8]">{item.current ? 'Current version' : `Version ${item.version}`}</span>
              <span className="text-[12px] text-[#6E6E6E]">{author(item)} · {when(item.createdAt)}</span>
            </button>
          </li>
        ))}
        {versions.length === 1 && <li className="px-3 py-2 text-[12px] text-[#6E6E6E]">No earlier version yet. One is kept each time someone starts editing.</li>}
      </ol>
      <div className="flex min-w-0 flex-1 flex-col">
        {selected && (
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-[#2A2A2A] px-3 text-[12px] text-[#909090]">
            <span>Changes from version {selected.version} to the current version</span>
            {canRestore && (
              <button type="button" onClick={() => void restore()} disabled={restoring}
                className="ml-auto h-7 rounded-full bg-[#2A2A2A] px-3 text-[12px] text-[#E8E8E8] transition-colors duration-150 hover:bg-[#3A3A3A] disabled:opacity-50">
                {restoring ? 'Restoring…' : 'Restore this version'}
              </button>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto py-2">
          {diff.map((line, index) => (
            <div key={index} className={`flex px-3 ${line.kind === 'added' ? 'bg-[#4DAB9A]/15' : line.kind === 'removed' ? 'bg-[#E5484D]/15' : ''}`}>
              <code className={`w-4 shrink-0 select-none ${line.kind === 'added' ? 'text-[#4DAB9A]' : 'text-[#E5484D]'}`} style={MONO}>
                {line.kind === 'added' ? '+' : line.kind === 'removed' ? '-' : ''}
              </code>
              <code className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[#D4D4D4]" style={MONO}>{line.text || ' '}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
